import React, {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import PageGroupModel from '@mm/state/model/PageGroupModel';
import PageModel from '@mm/state/model/PageModel';
import modalActions from '@mm/state/modules/modalsState/actions';
import {
  selectPageGroups,
  selectPages,
} from '@mm/state/selectors/pageSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { SearchInput } from '../../components/SearchInput/SearchInput';
import css from './NewDialoguePickerModal.module.css';
import { DialoguePickerModalPropsNew } from '@mm/state/modules/modalsState/props';
import { Button, Heading, ScrollArea, Spinner } from '@mm/components/src';
import classNames from 'classnames';
import { CloseCircleIcon, PlusCircleIcon } from '@mm/icons/general';
import PageEntity from '../../../editor/features/build/requestModels/PageEntity';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { generatePageStructure } from '@mm/state/helpers/filterable-select-helpers';
import { usePageGroupCRUDActions } from '@mm/state/actions/PageGroupCRUDActions';
import { usePageCRUDActions } from '@mm/state/actions/PageCRUDActions';
import { colors } from '../../../editor/features/beta/styles/colors';
import PrimarySelect, {
  DropdownOption,
} from '../../components/PrimarySelect/PrimarySelect';
import { ReactComponent as FolderIcon } from 'assets/icons/open-folder.svg';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import { OnClickElsewhere } from '../../components/OnClickElsewhere/OnClickElsewhere';
import TextInput from '../../components/TextInput';
import { PrimaryButton } from '../../buttons/PrimaryButton/PrimaryButton';

const useDialogueOptions = (selectedId?: number) => {
  const activeChatbotId = useSelector(activeChatbotIdSelector);

  const dialogues = useSelector(selectPages);
  const dialogueFolders = useSelector(selectPageGroups);

  const pageGroupCRUDActions = usePageGroupCRUDActions();
  const widgetsCRUDActions = useWidgetCRUDActions();
  const pageCRUDActions = usePageCRUDActions();

  const pages = generatePageStructure(dialogues, dialogueFolders);

  const options = useMemo(() => {
    if (!selectedId) {
      return pages;
    }

    const groupIdx = pages.findIndex(group =>
      group.suggestions?.find(i => i?.id === selectedId),
    );

    if (groupIdx === -1) {
      return pages;
    }

    const group = {
      ...pages[groupIdx],
      suggestions: pages[groupIdx]?.suggestions
        .sort((l, r) => {
          if (l?.id === selectedId) {
            return 1;
          }

          return 0;
        })
        .reverse(),
    };

    return [
      group,
      ...pages.slice(0, groupIdx),
      ...pages.slice(groupIdx + 1, pages.length),
    ];
  }, [selectedId, pages]);

  const createDialogue = useCallback(
    async ({ name, pageGroupId }: { name: string; pageGroupId: number }) => {
      const entity = PageEntity.make(activeChatbotId, pageGroupId, name, '', 0);
      return pageCRUDActions.create(entity);
    },
    [activeChatbotId],
  );

  const cloneDialogue = useCallback(
    async (dialogue: PageModel) => {
      const res = await pageCRUDActions.clone({
        chatbotId: activeChatbotId,
        pageId: dialogue.id,
        targetPageId: undefined,
      });

      const clone = res as unknown as PageModel;

      if (clone?.id) {
        await widgetsCRUDActions.retrieveAll(clone.id);
      }

      return clone;
    },
    [activeChatbotId],
  );

  useEffect(() => {
    if (dialogues.length === 0 || dialogueFolders.length === 0) {
      pageCRUDActions.retrieveAll(activeChatbotId);
      pageGroupCRUDActions.retrieveAll(activeChatbotId);
    }
  }, []);

  return { options, createDialogue, cloneDialogue };
};
const useCreatePageGroup = (
  setSelectedFolder: (value: PageGroupModel) => void,
) => {
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const [newFolderName, setNewFolderName] = useState('');
  const [showSearchRightSide, setShowSearchRightSide] = useState(true);
  const [showUnderSearchInsert, setShowUnderSearchInsert] = useState(false);
  const [closeDropdown, setCloseDropdown] = useState(false);
  const pageGroupCRUDActions = usePageGroupCRUDActions();
  const pageGroups = useSelector(
    (state: StoreState) => state.pageGroup.entities,
  );
  const maxSortOrder = Math.max(
    ...pageGroups.map(pageGroup => pageGroup.sortOrder),
    0,
  );

  const searchRightSide = useMemo(
    () => (
      <div
        className={css.newFolder}
        onClick={() => {
          setShowUnderSearchInsert(true);
          setShowSearchRightSide(false);
        }}
      >
        {showSearchRightSide && '+ New Folder'}
      </div>
    ),
    [showSearchRightSide, setShowSearchRightSide, setShowUnderSearchInsert],
  );

  const underSearchInsert = useMemo(() => {
    const createNewFolder = async () => {
      const response = await pageGroupCRUDActions.create({
        botId: activeChatbotId,
        name: newFolderName,
        sortOrder: maxSortOrder + 1,
      });
      if (response?.success) {
        setShowUnderSearchInsert(false);
        setSelectedFolder(response.pageGroup);
        setCloseDropdown(true);
      }
      setCloseDropdown(false);
    };

    return showUnderSearchInsert ? (
      <div className={css.newFolderContainer}>
        <TextInput
          className={css.newFolderInput}
          value={newFolderName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setNewFolderName(e.target.value)
          }
          placeholder="Name New Folder"
          maxLength={50}
          autoFocus
        />
        <PrimaryButton
          className={css.createFolderButton}
          onClick={createNewFolder}
          size="md"
          disabled={!newFolderName?.length}
        >
          Create
        </PrimaryButton>
      </div>
    ) : undefined;
  }, [
    showUnderSearchInsert,
    newFolderName,
    setNewFolderName,
    activeChatbotId,
    pageGroupCRUDActions,
    setSelectedFolder,
  ]);
  return { underSearchInsert, searchRightSide, closeDropdown };
};

export const NewDialoguePickerModal = ({
  onDialogueSelected,
  selectedDialogueId,
  withNoOption = false,
}: DialoguePickerModalPropsNew) => {
  const [busy, setBusy] = useState(false);

  const [searchPhrase, setSearchPhrase] = useState('');
  const [create, setCreate] = useState(false);

  const [isValidName, setIsValidName] = useState(true);
  const [name, setName] = useState('');

  const { options, createDialogue, cloneDialogue } =
    useDialogueOptions(selectedDialogueId);

  const allFolders = useSelector(selectPageGroups);
  const dialogueFolders = allFolders.filter(({ id }) => !!id);

  const [selectedFolder, setSelectedFolder] = useState<PageGroupModel>(
    dialogueFolders[0],
  );

  const defaultSelected = selectedDialogueId
    ? ({ id: selectedDialogueId, name: '' } as PageModel)
    : ({ id: -1, name: 'No' } as PageModel);

  const [selectedDialogue, setSelectedDialogue] = useState<
    undefined | PageModel
  >(defaultSelected);

  const dispatch: Dispatch<StoreState> = useDispatch();
  const closeModal = () => {
    if (!busy) {
      dispatch(modalActions.closeModal());
    }
  };

  const { underSearchInsert, searchRightSide, closeDropdown } =
    useCreatePageGroup(setSelectedFolder);

  const groups = useMemo(() => {
    const out = options
      .map(group => {
        const topMatch = group.title
          .toLowerCase()
          .includes(searchPhrase.toLowerCase());
        const suggestionsMatch = group.suggestions.filter(
          (dialogue: PageModel) =>
            [dialogue.name, dialogue.id].some(v =>
              v
                .toString()
                .toLowerCase()
                .includes(searchPhrase?.toLowerCase?.()),
            ),
        );

        if (topMatch) {
          return group;
        }

        if (suggestionsMatch.length) {
          return { ...group, suggestions: suggestionsMatch };
        }

        return undefined;
      })
      .filter(Boolean);
    return out;
  }, [options, searchPhrase, selectedDialogue]);

  const onDialogueClick = useCallback(
    (dialogue?: PageModel) => {
      if (selectedDialogue?.id === dialogue?.id) {
        return setSelectedDialogue(undefined);
      }

      return setSelectedDialogue(dialogue);
    },
    [selectedDialogue],
  );

  const onEditOriginalClick = useCallback(
    (e?: MouseEvent<HTMLButtonElement>) => {
      closeModal();
      onDialogueSelected(e, selectedDialogue);
    },
    [onDialogueSelected, closeModal, selectedDialogue],
  );

  const onMakeCopyClick = useCallback(
    async (e?: MouseEvent<HTMLButtonElement>) => {
      if (!selectedDialogue) {
        return;
      }

      setBusy(true);
      try {
        const res = await cloneDialogue(selectedDialogue);
        if (res.id) {
          closeModal();
          onDialogueSelected(e, res);
        }
      } catch (err) {
      } finally {
        setBusy(false);
      }
    },
    [onDialogueSelected, closeModal, selectedDialogue, cloneDialogue],
  );

  const onCancelBtnClick = useCallback(() => {
    setCreate(false);
  }, []);

  const onCreateBtnClick = useCallback(async () => {
    if (!name || !selectedFolder?.id) {
      return;
    }

    setBusy(true);

    try {
      const res = await createDialogue({
        name,
        pageGroupId: selectedFolder.id,
      });
      closeModal();
      onDialogueSelected(undefined, res.page);
    } catch (err) {
    } finally {
      setBusy(false);
    }
  }, [onDialogueSelected, closeModal, selectedFolder, name]);

  const onNewDialogueBtnClick = useCallback(async () => {
    setCreate(true);
  }, []);

  const checkIsNameValid = useCallback(() => {
    const isValid = !!name.trim().length;
    setIsValidName(isValid);
    return isValid;
  }, [name]);

  const customOptionRenderer = (option: undefined | DropdownOption) =>
    option ? (
      <div className="dialogue-picker-modal__folder-option">
        <FolderIcon />
        <div className="dialogue-picker-modal__folder-option-name">
          {option.name}
        </div>
      </div>
    ) : (
      <></>
    );

  if (create) {
    return (
      <OnClickElsewhere handleClick={closeModal}>
        <form className={css.container}>
          {busy && (
            <Spinner className={css.spinner} standalone size={24} width={24} />
          )}
          <span className={css.close} onClick={closeModal}>
            <CloseCircleIcon />
          </span>
          <div className={css.body}>
            <Heading size={2}>Creating new Dialogue</Heading>
          </div>

          <div className={css.field}>
            <label htmlFor="id">
              <span className={css.label}>Dialogue Name</span>
            </label>
            <input
              id="name"
              className={classNames(css.input, {
                [css.error]: !isValidName,
              })}
              placeholder="Your name"
              value={name}
              onChange={e => setName(e.target.value)}
              onBlur={checkIsNameValid}
            />
          </div>

          <div className={css.field}>
            <span className={css.label}>Folder</span>
            <PrimarySelect
              options={dialogueFolders}
              showSearchFilter
              searchRightSide={searchRightSide}
              shouldCloseDropdown={closeDropdown}
              underSearchInsert={underSearchInsert}
              onChange={(option: PageGroupModel) => setSelectedFolder(option)}
              value={selectedFolder}
              placeholder="Filter by folder (optional)"
              customOptionRenderer={customOptionRenderer}
              customDropdownStyles={{ zIndex: '99999999' }}
            />
          </div>

          <div className={css.footer}>
            <Button
              color="transparent"
              onClick={onCancelBtnClick}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              onClick={onCreateBtnClick}
              disabled={!selectedFolder || !isValidName || busy}
            >
              Create
            </Button>
          </div>
        </form>
      </OnClickElsewhere>
    );
  }

  return (
    <OnClickElsewhere handleClick={closeModal}>
      <div className={css.container}>
        <span className={css.close} onClick={closeModal}>
          <CloseCircleIcon />
        </span>

        <div className={css.body}>
          {busy && (
            <Spinner
              show={busy}
              standalone
              className={css.spinner}
              size={24}
              width={24}
            />
          )}

          <div className={css.list}>
            <div className={css.top}>
              <Heading size={2}>Choose Dialogue</Heading>
              <Button
                color="transparent"
                onClick={onNewDialogueBtnClick}
                leftIcon={<PlusCircleIcon color={colors.accentBlue} />}
              >
                Create Dialogue
              </Button>
            </div>

            <SearchInput
              placeholder="Search"
              value={searchPhrase}
              onChange={e => setSearchPhrase(e.target.value)}
              className={css.search}
            />

            <ScrollArea className={css.dialogues}>
              {!!groups.length && (
                <>
                  {withNoOption && (
                    <div className={css.group}>
                      <div
                        className={classNames(css.dialogue, {
                          [css.active]:
                            !selectedDialogue || selectedDialogue?.id === -1,
                        })}
                        onClick={() => onDialogueClick({ id: -1 } as PageModel)}
                      >
                        No dialogue
                      </div>
                    </div>
                  )}

                  {groups.map(group => (
                    <div className={css.group}>
                      <Heading className={css.title} size={5}>
                        {group.title}
                      </Heading>

                      {group.suggestions.map(dialogue => (
                        <div
                          key={dialogue.id}
                          className={classNames(css.dialogue, {
                            [css.active]: selectedDialogue?.id === dialogue.id,
                          })}
                          onClick={() => onDialogueClick(dialogue)}
                        >
                          {dialogue.name}
                        </div>
                      ))}
                    </div>
                  ))}
                </>
              )}

              {!groups.length && !searchPhrase && (
                <div className={css.empty}>No dialogues created yet!</div>
              )}

              {!groups.length && searchPhrase && (
                <div className={css.empty}>Nothing found...</div>
              )}
            </ScrollArea>

            <div className={css.preview}>Preview </div>
          </div>
        </div>
        <div className={css.footer}>
          <Button
            color="transparent"
            onClick={onMakeCopyClick}
            disabled={!selectedDialogue || selectedDialogue.id === -1 || busy}
          >
            Make a copy
          </Button>
          <Button onClick={onEditOriginalClick} disabled={busy}>
            Select
          </Button>
        </div>
      </div>
    </OnClickElsewhere>
  );
};
